/* istanbul ignore file */

/**
 * CLONE OF FORMWRAPPER. IT IS CURRENTLY ONLY MEANT FOR READ ONLY FORMS> DO NOT TRY MUTATIONS
 */
import React, { Component, PureComponent, Children, cloneElement } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import {
  Icon,
  Button,
  Loader,
  Dropdown,
  withApplicationNotificationsContext,
} from '@adc/ui-core'
import { print } from 'graphql'
import fetch from '@adc/fetch'
import PageLayout from '../pageLayout'
import { submit as submitValues, requiredIf } from '../../utils'
import memoizedDebounce from '../../utils/memoizedDebounce'
import ConfirmationModal from '../confirmationModal'
import UnsavedChangesModal from '../unsavedChangesModal'
import { pagePropTypes } from './index'

// forAuctionGraph expects a query string. Callers may pass a graphql-tag
// AST (gql``). Normalize both, preferring the original source text carried
// on the AST's loc, and falling back to print() for safety.
const toQueryString = query => {
  if (typeof query === 'string') return query
  return query?.loc?.source?.body ?? print(query)
}

// Build an error whose shape (graphQLErrors / networkError) matches what
// errorHandler implementations read.
const toGraphError = ({ graphQLErrors, networkError }) => {
  const message =
    graphQLErrors?.[0]?.message || networkError?.message || 'GraphQL error'
  return Object.assign(new Error(message), {
    graphQLErrors: graphQLErrors || [],
    networkError: networkError || null,
  })
}

class ApolloFormWrapper extends Component {
  static propTypes = {
    ...pagePropTypes,
    // form specific data processing
    defaultValues: PropTypes.object.isRequired,
    uiToApi: PropTypes.func.isRequired,
    apiToUi: PropTypes.func.isRequired,
    // providers
    postRecord: requiredIf(PropTypes.func, props => !props.viewOnly),
    putRecord: requiredIf(PropTypes.func, props => !props.viewOnly),
    // entity specific ui
    crumbs: PropTypes.array.isRequired, // e.g. [ROOT_CRUMB, VENUE_TEMPLATES_CRUMB]
    entityName: PropTypes.string.isRequired, // e.g. 'Venue Template'
    entityDynamicTitle: PropTypes.node, // e.g. <span>Some dynamic inline stuff</span>
    titleAllowOverflow: PropTypes.bool,
    entityListRoute: PropTypes.string.isRequired, // e.g. '/venue-templates'
    primaryKey: PropTypes.string.isRequired, // e.g. 'venue_template_id'
    alternativeRoute: PropTypes.string, // Allows to set the url to change when the form is done
    children: PropTypes.node.isRequired, // form component
    showAuditHistory: PropTypes.bool,
    showMoreOptions: PropTypes.bool,
    moreOptions: PropTypes.array,
    cancelButtonText: PropTypes.string,
    saveButtonText: PropTypes.string,
    customButtons: PropTypes.array,
    subheader: PropTypes.any,
    crumbsFullWidth: PropTypes.bool,
    errorHandler: PropTypes.func.isRequired,
    notifications: PropTypes.object,
  }

  static defaultProps = {
    viewOnly: false,
    customButtons: [],
    saveButtonText: 'Save',
    moreOptions: [],
    subheader: null,
    crumbsFullWidth: true,
    showMoreOptions: false,
    showAuditHistory: false,
    notifications: {},
  }

  constructor(props) {
    super(props)
    const { match } = props
    const isEdit = Boolean(match && match.params && !_.isNil(match.params.id))
    this.state = {
      confirmationModalOpen: false,
      unsavedChangesModalOpen: false,
      recordId: null,
      initialValues: props.defaultValues,
      hasReceivedInitialData: false,
      // query state
      loading: isEdit,
      error: null,
      data: null,
    }
  }

  componentDidMount() {
    const { match } = this.props
    if (match && match.params && !_.isNil(match.params.id)) {
      this.setState({ recordId: parseInt(match.params.id, 10) }, this.runQuery)
    }
  }

  runQuery = async () => {
    if (!this.isEditMode) return
    const { query, errorHandler } = this.props
    const { recordId } = this.state
    this.setState({ loading: true })

    let data = null
    let error = null
    try {
      const res = await fetch.forAuctionGraph({
        query: toQueryString(query),
        variables: { recordId },
      })
      const body = res?.body || {}
      data = body.data || null
      if (body.errors?.length) {
        error = toGraphError({ graphQLErrors: body.errors })
      }
    } catch (e) {
      // Non-2xx responses still surface a parsed body on the thrown error.
      data = e?.body?.data || null
      error = toGraphError({
        graphQLErrors: e?.body?.errors,
        networkError: e,
      })
    }

    if (error && !errorHandler(error, data, this.toastOpenDebounced)) {
      this.onDone() // abort!
      return
    }

    this.setState({ loading: false, data, error })
  }

  get isEditMode() {
    // treat as edit mode when id is passed in param AND we've got it into state properly
    const { match } = this.props
    return Boolean(
      match &&
        match.params &&
        !_.isNil(match.params.id) &&
        !_.isNil(this.state.recordId)
    )
  }

  getEntityEditUrl() {
    const { recordId } = this.state
    const { entityListRoute } = this.props
    return `${entityListRoute}/${recordId}/edit`
  }

  // TODO: room for improvement would be to drop requests with empty data, unless we are sure there will
  // be requests, currently POST or PUT, that will have no data.
  onSubmit = async (values, redirectUrl) => {
    const {
      updateRecordInList,
      clearList,
      apiToUi,
      uiToApi,
      putRecord,
      postRecord,
      primaryKey,
      history,
    } = this.props
    /**
     * Here we pass initialValues to let apps decide if they can spare
     * sending some data if it didn't change
     */
    const apiValues = uiToApi(values, this.state.initialValues)
    const result = await submitValues(
      this.isEditMode ? putRecord : postRecord,
      apiValues
    )

    const recordId = result[primaryKey]
    this.setState(
      {
        recordId,
        confirmationModalOpen: true,
        initialValues: apiToUi(result), // must set to response result for latest updated info
        // (e.g. mainEntity.childEntity.primaryKey)
      },
      () => {
        if (this.isEditMode) {
          // list uses API version of values (snake case'd)
          updateRecordInList(result)
        } else {
          // clear out to force reload
          clearList()
        }

        if (_.isString(redirectUrl) && !_.isEmpty(redirectUrl)) {
          const parentEntityPrimaryKey = `${_.camelCase(
            primaryKey
          )}=${recordId}`
          const alternativeRoute = `alternativeRoute=${this.getEntityEditUrl()}`
          history.push(
            `${redirectUrl}?${parentEntityPrimaryKey}&${alternativeRoute}`
          )
        }
      }
    )
  }

  closeConfirmationModal = () => {
    this.setState({ confirmationModalOpen: false })
  }

  closeUnsavedChangesModal = () => {
    this.setState({ unsavedChangesModalOpen: false })
  }

  openUnsavedChangesModal = () => {
    this.setState({ unsavedChangesModalOpen: true })
  }

  onCreateAnother = () => {
    const { reset, defaultValues } = this.props
    // go back to default values and reset form
    this.setState({ initialValues: defaultValues }, () => {
      reset()
      this.closeConfirmationModal()
    })
  }

  onKeepEditing = () => {
    const { history, reset, alternativeRoute } = this.props
    if (!this.isEditMode) {
      const alternativeRouteParam = alternativeRoute
        ? `?alternativeRoute=${alternativeRoute}`
        : ''
      history.push(`${this.getEntityEditUrl()}${alternativeRouteParam}`)
    }
    reset()
    this.closeConfirmationModal()
  }

  onDone = () => {
    const { history, entityListRoute, alternativeRoute } = this.props
    const route = alternativeRoute || entityListRoute
    history.push(route)
  }

  // this is debounced and memoized so we don't get into a render loop and
  // don't show same message over and over.
  toastOpenDebounced = memoizedDebounce(
    (...args) => {
      const { notifications } = this.props
      notifications.toast?.open(...args)
    },
    0,
    {
      resolver: options => {
        return options?.message || options?.error?.message
      },
    }
  )

  render() {
    const {
      submit,
      pristine,
      submitting,
      change,
      crumbs,
      entityName,
      showMoreOptions,
      showAuditHistory,
      moreOptions,
      entityDynamicTitle,
      entityListRoute,
      children,
      history,
      titleAllowOverflow,
      alternativeRoute,
      viewOnly,
      cancelButtonText,
      saveButtonText,
      customButtons,
      subheader,
      crumbsFullWidth,
    } = this.props
    const {
      confirmationModalOpen,
      unsavedChangesModalOpen,
      recordId,
      loading,
      error,
      data,
    } = this.state
    const title = `${this.isEditMode ? 'Manage' : 'Create'} ${entityName}`
    const crumbsWithTitle = [...crumbs, title]

    const cancelRoute = alternativeRoute || entityListRoute

    const saveButton = viewOnly
      ? []
      : [
          <Button
            role='primary'
            size='small'
            style={{ marginLeft: '0.75em' }}
            disabled={pristine || submitting}
            loading={submitting}
            onClick={submit}
            elmId='save_button'
            key='save_button'
          >
            {saveButtonText}
          </Button>,
        ]

    const trigger = <Icon name='ellipsis vertical' />

    const AuditHistoryLink =
      this.isEditMode && showAuditHistory
        ? [
            {
              key: 'user',
              text: 'Audit History',
              onClick: () =>
                history.push(`${entityListRoute}/${recordId}/history`),
              'data-elm-id': 'audit_history',
            },
          ]
        : []
    const options = [...AuditHistoryLink, ...moreOptions]

    const goToCancelRoute = () => {
      history.push(cancelRoute)
    }

    const buttons = [
      ...customButtons,
      ...saveButton,
      <Button
        size='small'
        style={{ marginLeft: '0.75em' }}
        elmId='cancel_button'
        key='cancel_button'
        onClick={
          viewOnly || pristine ? goToCancelRoute : this.openUnsavedChangesModal
        }
      >
        {cancelButtonText ||
          ((this.isEditMode && pristine) || viewOnly ? 'Back' : 'Cancel')}
      </Button>,
      showMoreOptions && (
        <Dropdown
          className='u-ml-2 morpheus-kebab-menu'
          trigger={trigger}
          options={options}
          direction='left'
          icon={null}
          data-elm-id='more_options'
        />
      ),
    ]

    return (
      <PageLayout
        title={
          entityDynamicTitle ||
          `${this.isEditMode ? 'Manage' : 'Create'} ${entityName}`
        }
        crumbs={crumbsWithTitle}
        buttons={buttons}
        subheader={subheader}
        crumbsFullWidth={crumbsFullWidth}
        titleAllowOverflow={titleAllowOverflow}
      >
        <ConfirmationModal
          entityName={entityName}
          isEditMode={this.isEditMode}
          isModalOpen={confirmationModalOpen}
          recordId={recordId}
          onModalClose={this.closeConfirmationModal}
          onCreateAnother={this.onCreateAnother}
          onKeepEditing={this.onKeepEditing}
          onDone={this.onDone}
        />
        <UnsavedChangesModal
          allowContinueWithoutSaving
          isModalOpen={unsavedChangesModalOpen}
          onContinueWithoutSaving={goToCancelRoute}
          onKeepEditing={this.closeUnsavedChangesModal}
        />
        {(() => {
          const initialValues =
            _.isEmpty(this.state.initialValues) && !_.isEmpty(data)
              ? this.props.apiToUi(data)
              : this.state.initialValues
          // Stop calling apiToUi with data that hasn't been filled yet, since that forces those translations to
          // guards themselves up to their neck.
          const formProps = {
            change,
            history,
            onSubmit: this.onSubmit,
            initialValues: loading || _.isEmpty(data) ? {} : initialValues,
            isEditMode: this.isEditMode,
          }

          const childrenWithProps = Children.map(children, child =>
            cloneElement(child, formProps)
          )

          return (
            <QueryChildrenRenderer loading={loading} error={error} data={data}>
              {childrenWithProps}
            </QueryChildrenRenderer>
          )
        })()}
      </PageLayout>
    )
  }
}

// The initial render happens with loading false, since the request is not in
// flight yet. To prevent content flashing when this component is mounted, we
// track the first time a request succeeds and we have some data.
//
// NOTE: This won't work for mutations where a 204 response has no body and so will always be empty
class QueryChildrenRenderer extends PureComponent {
  static propTypes = {
    loading: PropTypes.bool,
    data: PropTypes.any,
    error: PropTypes.any,
    children: PropTypes.node,
  }

  state = {
    hasReceivedInitialData: false,
  }

  static getDerivedStateFromProps({ loading, error, data }, state) {
    if (!loading && data && !state.hasReceivedInitialData) {
      return {
        hasReceivedInitialData: true,
      }
    }

    return null
  }

  render() {
    const { loading, data, children } = this.props

    const waitingState =
      loading || !this.state.hasReceivedInitialData || _.isEmpty(data)

    return (
      <>
        <Loader active={waitingState} inverted />
        {waitingState ? null : children}
      </>
    )
  }
}

export default withApplicationNotificationsContext(ApolloFormWrapper)
